import { Injectable } from "@angular/core";
import { Action, State, StateContext } from "@ngxs/store";
import { MerchantOrder } from "src/app/_entities/merchant-order/merchant-order.modal";
import { DateRange, OrdersFiltersModel, StatusTabs } from "src/app/_models/self-ship/orders/orders-filters.model";
import { OrdersService } from "src/app/_services/orders.service";
import { LoadOrderCounts, LoadOrders, SyncOrder, SyncOrders, UpdateDateRange, UpdateOrdersFilters } from "./merchant-order.action";
import moment from 'moment';
import { forkJoin, Observable, of } from "rxjs";
import { catchError, finalize, tap } from 'rxjs/operators';
import { createEntityCollections } from "@angular-ru/cdk/entity";
import { OrderCount, OrdersFetchFilters } from "src/app/_services/self-ship/self-ship-orders.service";
import { RealTimeService } from "src/app/_services/real-time-connection/real-time.service";
import { HubClientMethods } from "src/app/_services/real-time-connection/real-time-connection.constants";
import IEntityChange from "src/app/_interfaces/i-entity-change";
import { StatesHelper } from "src/app/states/states.helper";

export const defaultOrderCount: OrderCount = {
  total: 0,
  open: 0,
  picked: 0,
  packed: 0,
  partiallyShipped: 0,
  shipped: 0
};

export interface OrdersStateModel {
  data: {
    [status in StatusTabs]?: {
      [dateKey: string]: MerchantOrder[];
    };
  };
  filters: OrdersFiltersModel;
  orderCount: OrderCount;
  loading: boolean;
}

@State<OrdersStateModel>({
  name: 'merchantOrders',
  defaults: {
    ...createEntityCollections(),
    data: {},
    filters: { ...new OrdersFiltersModel() },
    orderCount: { ...defaultOrderCount },
    loading: false
  }
})
@Injectable()
export class MerchantOrderState {
  constructor(
    private ordersService: OrdersService,
    private realtimeService: RealTimeService,) {
  }

  @Action(UpdateOrdersFilters)
  updateFilters(ctx: StateContext<OrdersStateModel>, { filters }: UpdateOrdersFilters) {
    const state = ctx.getState();
    ctx.patchState({
      filters: {
        ...state.filters,
        ...filters
      }
    });
    ctx.dispatch(new LoadOrders());
  }

  // if only date range changed then reload orderCount
  @Action(UpdateDateRange)
  updateDateRange(ctx: StateContext<OrdersStateModel>, { dateRange }: UpdateDateRange) {
    const state = ctx.getState();
    ctx.patchState({
      filters: {
        ...state.filters,
        dateRange
      }
    });
    ctx.dispatch(new LoadOrderCounts());
  }

  @Action(LoadOrderCounts, { cancelUncompleted: true })
  loadOrderCounts(ctx: StateContext<OrdersStateModel>, { loadOrder }: LoadOrderCounts) {
    const filter = ctx.getState().filters;
    const payload = mapToOrdersFetchFilters(filter);
    delete payload.status;
    return this.ordersService.getTotalOrdersCount(payload).pipe(
      tap((orderCount) => {
        ctx.patchState({ orderCount: { ...orderCount } });
        if (loadOrder) {
          ctx.dispatch(new LoadOrders(true));
        }
      }),
      catchError(() => of(null))
    );
  }

  @Action(LoadOrders, { cancelUncompleted: true })
  loadOrders(ctx: StateContext<OrdersStateModel>, { reload }: LoadOrders) {
    const state = ctx.getState();
    const key = buildDateKey(state.filters.dateRange);
    const status = state.filters.selectedStatusTab;
    const existing = state.data?.[status]?.[key];
    const expectedCount = getStatusCount(state.orderCount, status);

    // Ensure realtime handler is registered before any early returns
    this.realtimeService.registerHandler(HubClientMethods.SyncOrder, (notification: IEntityChange<MerchantOrder>) => {
      ctx.dispatch(new SyncOrder(notification));
    });
    this.realtimeService.registerHandler(HubClientMethods.SyncOrders, (notifications: IEntityChange<MerchantOrder>[]) => {
      ctx.dispatch(new SyncOrders(notifications));
    });

    // If we have cached data and counts match, avoid API call
    if (existing && !reload && existing.length === expectedCount) {
      return;
    }

    // If expected count is zero, ensure state reflects empty list and skip API
    if (!reload && expectedCount === 0) {
      ctx.patchState({
        data: {
          ...state.data,
          [status]: { ...(state.data[status] || {}), [key]: [] }
        }
      });
      return;
    }

    const batchSize = 500;
    const apiObservables: Array<Observable<MerchantOrder[]>> = [];
    const totalOrder = expectedCount || 0;

    for (let i = 0; i < Math.ceil(totalOrder / batchSize); i++) {
      apiObservables.push(this.ordersService.getOrders(mapToOrdersFetchFilters(state.filters), i + 1, batchSize) as unknown as Observable<MerchantOrder[]>);
    }

    if (apiObservables.length) {
      ctx.patchState({ loading: true });
      return forkJoin(apiObservables).pipe(
        tap((result: Array<MerchantOrder[]>) => {
          const orders = result.flatMap(arr => arr);
          const carrierOptions = [...new Set(orders.map(order => order.shippingMethod).filter(o => !!o))];
          const tagOptions = [...new Set(orders.flatMap(order => order.tags).filter(o => !!o))];

          // Re-read latest state to avoid writing into stale tab/date if filters changed
          const latestState = ctx.getState();
          const latestKey = buildDateKey(latestState.filters.dateRange);
          const latestTab = latestState.filters.selectedStatusTab;

          ctx.patchState({
            data: {
              ...latestState.data,
              [latestTab]: { ...(latestState.data[latestTab] || {}), [latestKey]: orders }
            },
            filters: {
              ...latestState.filters,
              carrierOptions,
              tagOptions
            }
          });
        }),
        catchError(() => of([] as unknown as Array<MerchantOrder[]>)),
        finalize(() => ctx.patchState({ loading: false }))
      );
    }

    // handler already registered above
  }

  @Action(SyncOrder)
  syncOrder(ctx: StateContext<OrdersStateModel>, { notification }: SyncOrder) {
    const state = ctx.getState();
    const filters = state.filters;
    const key = buildDateKey(filters.dateRange);
    const tab = filters.selectedStatusTab;

    const existingOrders = state.data?.[tab]?.[key] ?? [];

    // Always refresh counts for the active range (e.g. label recovery, status changes)
    const countFilters = { ...mapToOrdersFetchFilters(filters) } as any;
    delete countFilters.status;
    this.ordersService.getTotalOrdersCount(countFilters).subscribe((orderCount) => {
      const latest = ctx.getState();
      ctx.patchState({ orderCount: { ...orderCount }, loading: latest.loading });
    });

    // Reload current tab only if the order moves in/out of the current tab (count change scenario)
    const existsInCurrentList = existingOrders.some(o => o.id === notification.id && orderBelongsToActiveTabView(o, filters));
    const newBelongsToCurrent = !!notification.data && orderBelongsToActiveTabView(notification.data as MerchantOrder, filters);
    const shouldReloadCurrentTab =
      (!notification.data && existsInCurrentList) ||
      (!!notification.data && (existsInCurrentList !== newBelongsToCurrent));

    if (shouldReloadCurrentTab) {
      ctx.dispatch(new LoadOrderCounts(true));
      return;
    }

    // Same tab/filter set (e.g. shipping label document arrived) — update the visible row in place.
    if (notification.data && newBelongsToCurrent) {
      const updatedMap: { [id: number]: MerchantOrder } = existingOrders.reduce((acc, o) => (acc[o.id] = o, acc), {} as any);
      updatedMap[notification.id] = notification.data as MerchantOrder;
      ctx.patchState({
        data: {
          ...state.data,
          [tab]: {
            ...(state.data[tab] || {}),
            [key]: Object.values(updatedMap)
          }
        }
      });
    }
  }

  @Action(SyncOrders)
  syncOrders(ctx: StateContext<OrdersStateModel>, { notifications }: SyncOrders) {
    const state = ctx.getState();
    const filters = state.filters;
    const key = buildDateKey(filters.dateRange);
    const tab = filters.selectedStatusTab;

    const existingOrders = state.data?.[tab]?.[key] ?? [];

    // Always refresh tab counts for the active date range
    const countFilters = { ...mapToOrdersFetchFilters(filters) } as any;
    delete countFilters.status;
    this.ordersService.getTotalOrdersCount(countFilters).subscribe((orderCount) => {
      const latest = ctx.getState();
      ctx.patchState({ orderCount: { ...orderCount }, loading: latest.loading });
    });

    let requiresReload = false;

    for (const n of notifications) {
      const exists = existingOrders.some(o => o.id === n.id && orderBelongsToActiveTabView(o, filters));

      if (!n.data) {
        if (exists) {
          requiresReload = true;
          break;
        }
        continue;
      }

      const order = n.data as MerchantOrder;
      const newBelongsToCurrent = orderBelongsToActiveTabView(order, filters);

      if (exists !== newBelongsToCurrent) {
        requiresReload = true;
        break;
      }
    }

    if (requiresReload) {
      ctx.dispatch(new LoadOrderCounts(true));
      return;
    }

    // No presence changes → upsert any orders that still belong to the current tab/date range
    const latest = ctx.getState();
    const latestKey = buildDateKey(latest.filters.dateRange);
    const latestTab = latest.filters.selectedStatusTab;
    const latestFilters = latest.filters;
    const currentList = latest.data?.[latestTab]?.[latestKey] ?? [];

    const updatedMap: { [id: number]: MerchantOrder } = currentList.reduce((acc, o) => (acc[o.id] = o, acc), {} as any);
    let hasUpdate = false;

    for (const n of notifications) {
      if (!n.data) continue;
      const order = n.data as MerchantOrder;
      const belongs = orderBelongsToActiveTabView(order, latestFilters);
      if (!belongs) continue;
      updatedMap[order.id] = order;
      hasUpdate = true;
    }

    if (!hasUpdate) {
      return;
    }

    const updatedOrders = Object.values(updatedMap);

    ctx.patchState({
      data: {
        ...latest.data,
        [latestTab]: {
          ...(latest.data[latestTab] || {}),
          [latestKey]: updatedOrders
        }
      }
    });
  }
}


/** Order would appear in the grid for the selected status tab and date range (merchant orders view). */
export function orderBelongsToActiveTabView(
  order: MerchantOrder,
  filters: OrdersFiltersModel
): boolean {
  if (!order) return false;
  const { selectedStatusTab, dateRange, customer, storeIds, warehouseIds, selectedTags, selectedCarrierIds, searchTerm } = filters;
  const fromTs = new Date(dateRange.fromDate).getTime();
  const toTs = new Date(dateRange.toDate).getTime();
  const orderTs = new Date(order.orderDate as unknown as Date).getTime();
  if (orderTs < fromTs || orderTs > toTs) return false;
  if (selectedStatusTab !== 'All' && order.orderStatus !== selectedStatusTab) return false;
  if (customer?.trim() && !order.orderAddress?.name?.toLowerCase().includes(customer.trim().toLowerCase())) return false;
  if (storeIds?.length && !storeIds.includes(order.storeId)) return false;
  if (warehouseIds?.length && !warehouseIds.some(wi => order.items?.map(oi => oi.warehouseId).includes(+wi))) return false;
  if (selectedTags?.length && !selectedTags.some(tag => order.tags?.includes(tag))) return false;
  if (selectedCarrierIds?.length && !selectedCarrierIds.includes(order.shippingMethod)) return false;
  if (searchTerm?.trim() && !matchesSearch(order, searchTerm.trim())) return false;
  return true;
}

function matchesSearch(order: MerchantOrder, term: string): boolean {
  const lower = term.toLowerCase();
  if (order.orderNumber?.toLowerCase().includes(lower)) return true;
  if (order.orderAddress?.name?.toLowerCase().includes(lower)) return true;

  const productEntities = StatesHelper.productsEntitiesState?.entities || {} as Record<number, any>;
  return (order.items || []).some(it => {
    const sku: string = (productEntities as any)[it.productId]?.sku || '';
    if (!sku) return false;
    const skuLower = sku.toLowerCase();
    return skuLower === lower || skuLower.includes(lower);
  });
}

export function buildDateKey(range: DateRange): string {
  const from = moment(range.fromDate).format('YYYYMMDD');
  const to = moment(range.toDate).format('YYYYMMDD');
  return `${from}_${to}`;
}

function mapToOrdersFetchFilters(filters: OrdersFiltersModel): OrdersFetchFilters {
  const mapped: OrdersFetchFilters = {
    fromDate: filters.dateRange?.fromDate.toUTCString(),
    toDate: filters.dateRange?.toDate.toUTCString(),
    status: filters.selectedStatusTab ?? "All"
  };

  const customer = filters.customer?.trim();
  if (customer) {
    mapped.customer = customer;
  }

  return mapped;
}

function getStatusCount(orderCount: OrderCount, status: StatusTabs): number {
  switch (status) {
    case 'Open':
      return orderCount.open;
    case 'Picked':
      return orderCount.picked;
    case 'Packed':
      return orderCount.packed;
    case 'PartiallyShipped':
      return orderCount.partiallyShipped;
    case 'Shipped':
      return orderCount.shipped;
    case 'All':
    default:
      return orderCount.total;
  }
}